<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="onFilterStatus">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.shelve_on') }}</option>
              <option value="false">{{ $t('attributes.shelve_off') }}</option>
            </b-select>
          </b-field>
          <b-field>
            <b-select
              v-model="selectedArea"
              :loading="loadingAreas"
              :placeholder="$t('message.please_select') + $t('attributes.area')"
              @input="onSelectArea"
            >
              <option value="">
                {{ $t('attributes.all_area') }}
              </option>
              <option
                v-for="option in areas"
                :value="option"
                :key="option.code"
              >
                {{ !option.status ? '[下架]' : '' }}
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <b-input
              v-model="searchNo"
              :placeholder="
                $t('attributes.search') + $t('attributes.catalog_code')
              "
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
          <b-field>
            <b-input
              v-model="searchName"
              :placeholder="$t('attributes.search') + $t('attributes.name')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="isCreateModalActive = true"
          >
            {{ $t('attributes.add') + $t('attributes.catalog') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :selected.sync="selectedItem"
      :loading="isLoading"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      :row-class="rowClass"
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :current-page="parseInt(page)"
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @select="onSelect"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="sort"
        :label="$t('attributes.sort')"
        width="80"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.sort }}
      </b-table-column>

      <b-table-column
        field="catalog_no"
        :label="$t('attributes.catalog_code')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.catalog_no }}
      </b-table-column>

      <b-table-column
        field="status"
        :label="$t('attributes.activation_status')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <b-tag
          :type="props.row.status ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ shelveStatus(props.row.status) }}
        </b-tag>
      </b-table-column>

      <b-table-column
        field="link"
        :label="$t('attributes.catalog_type')"
        width="60"
        sortable
        v-slot="props"
      >
        {{ props.row.link ? '外連' : '一般' }}
      </b-table-column>

      <b-table-column
        :label="$t('attributes.catalog_cover')"
        width="80"
        centered
        v-slot="props"
      >
        <a
          :href="CatalogImage(props.row.picture)"
          @click.prevent="onClickImage(CatalogImage(props.row.picture))"
        >
          <figure class="image">
            <img
              :src="CatalogImage(props.row.picture)"
              :alt="$t('attributes.catalog_cover')"
              style="width: 64px; height: 43px;"
            />
          </figure>
        </a>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.operation_picture')"
        width="80"
        centered
        v-slot="props"
      >
        <a
          :href="CatalogImage(props.row.operation_picture)"
          @click.prevent="
            onClickImage(CatalogImage(props.row.operation_picture))
          "
        >
          <figure class="image">
            <img
              :src="CatalogImage(props.row.operation_picture)"
              :alt="$t('attributes.operation_picture')"
              style="width: 64px; height: 43px;"
            />
          </figure>
        </a>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.operation_mobile_picture')"
        width="80"
        centered
        v-slot="props"
      >
        <a
          :href="CatalogImage(props.row.operation_mobile_picture)"
          @click.prevent="
            onClickImage(CatalogImage(props.row.operation_mobile_picture))
          "
        >
          <figure class="image">
            <img
              :src="CatalogImage(props.row.operation_mobile_picture)"
              :alt="$t('attributes.operation_mobile_picture')"
              style="width: 64px; height: 43px;"
            />
          </figure>
        </a>
      </b-table-column>

      <b-table-column
        field="area_name"
        :visible="!selectedArea"
        :label="$t('attributes.area_name')"
        width="120"
        sortable
        v-slot="props"
      >
        {{ props.row.area_name }}
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        width="300"
        v-slot="props"
      >
        <p v-html="props.row.name"></p>
      </b-table-column>

      <b-table-column
        field="group"
        :label="$t('attributes.group')"
        width="200"
        v-slot="props"
      >
        <p v-html="props.row.region_list_name"></p>
      </b-table-column>

      <b-table-column
        field="product_valid_count"
        :label="$t('attributes.catalog_product_count')"
        sortable
        :width="120"
        v-slot="props"
      >
        {{ props.row.product_valid_count }} / {{ props.row.product_count }}
      </b-table-column>

      <b-table-column :width="180" label="功能" centered v-slot="props">
        <div class="buttons">
          <b-button
            type="is-info"
            size="is-small"
            @click.prevent.stop="onUpdateSort(props.row)"
          >
            {{ $t('attributes.product') + $t('attributes.sort') }}
          </b-button>
          <b-button
            type="is-danger"
            size="is-small"
            @click.prevent.stop="onDelete(props.row)"
          >
            {{ $t('attributes.delete') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isCreateModalActive"
      :can-cancel="['x']"
      scroll="keep"
      has-modal-card
    >
      <create-form :is-sending="sending" @onSend="onCreate" />
    </b-modal>

    <b-modal
      :active.sync="isProductListModelActive"
      :can-cancel="false"
      full-screen
      has-modal-card
    >
      <products-from
        :area-code="selectedCatalogAreaCode"
        :catalog-no="selectedCatalogNumber"
      />
    </b-modal>
  </section>
</template>

<script>
import { AREA_API, CATALOG_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import debounce from 'lodash/debounce'
import CreateForm from '@/components/catalogs/CreateForm'
import ProductsFrom from '@/components/catalogs/CatalogProductsFrom'

export default {
  name: 'CatalogList',
  components: {
    CreateForm,
    ProductsFrom
  },

  props: {
    query: {
      type: Object,
      required: true
    },
    selected: {
      type: Object
    },
    loadingItem: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      filterStatus: this.query.status || '',
      searchNo: this.query.no || '',
      searchName: this.query.search || '',
      sortField: this.query.sort || 'product_valid_count',
      sortOrder: this.query.order || 'asc',
      defaultSortOrder: 'asc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      loadingAreas: false,
      areas: [],
      selectedArea: null,
      selectedAreaCode: this.query.ac || null,

      isCreateModalActive: false,
      sending: false,

      isImageModalActive: false,
      selectedImageUrl: null,

      selectedCatalogAreaCode: null,
      selectedCatalogNumber: null,
      isProductListModelActive: false
    }
  },
  watch: {
    query(query) {
      this.getQuery(query)
      this.loadItems()
    },
    selected(item) {
      if (item) {
        this.items = this.items.map(x => {
          if (x.catalog_no === item.catalog_no) {
            return item
          } else {
            return x
          }
        })
      }
      this.selectedItem = item
    }
  },
  computed: {
    isLoading() {
      return this.loadingAreas || this.loadingItems || this.loadingItem
    }
  },
  mounted() {
    this.loadAreas()
  },
  methods: {
    async loadAreas() {
      try {
        this.loadingAreas = true
        const api = `${AREA_API}/list`
        const res = await this.axios.get(api)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.selectedArea = null

        if (this.selectedAreaCode) {
          this.selectedArea = areas.find(a => a.code === this.selectedAreaCode)
          this.selectedAreaCode = this.selectedArea && this.selectedArea.code
        }

        await this.loadItems()
      } catch (e) {
        this.areas = []
        this.selectedArea = null
        this.selectedAreaCode = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async loadItems() {
      try {
        const params = {
          active: this.filterStatus && this.filterStatus === 'true',
          area_code: this.selectedAreaCode,
          search_no: this.searchNo,
          search: this.searchName,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true

        const catalog_no = this.selectedItem && this.selectedItem.catalog_no

        const res = await this.axios.get(CATALOG_API, { params })
        const data = res.data.data
        this.items = data.items.sort(a => a.sort)
        this.total = data.item_count

        if (catalog_no) {
          const item = this.items.find(x => x.catalog_no === catalog_no)
          this.onSelect(item)
        }
      } catch (e) {
        this.items = []
        this.total = 0
        this.onSelect(null)

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.filterStatus = query.status || ''
      this.searchNo = query.no || ''
      this.searchName = query.search || ''
      this.sortField = query.sort || 'product_valid_count'
      this.sortOrder = query.order || 'asc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10

      this.selectedArea = null
      this.selectedAreaCode = query.ac || null

      if (this.selectedAreaCode) {
        this.selectedArea = this.areas.find(
          a => a.code === this.selectedAreaCode
        )
      }
    },
    updateQuery() {
      const query = {
        status: this.filterStatus,
        ac: this.selectedAreaCode,
        no: this.searchNo,
        search: this.searchName,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilterStatus() {
      this.onSelect(null)
      this.updateQuery()
    },
    onSelectArea(option) {
      this.selectedArea = option
      this.selectedAreaCode = option ? option.code : null

      this.onSelect(null)
      this.updateQuery()
    },
    onFilter: debounce(function() {
      this.onSelect(null)
      this.updateQuery()
    }, 500),
    onPageChange(page) {
      this.onSelect(null)
      this.page = page

      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order

      this.updateQuery()
    },
    onSelect(item) {
      this.selectedItem = item
      this.$emit('select', this.selectedItem)
      this.$emit('update:selected', this.selectedItem)
    },
    onUpdateSort(catalog) {
      this.selectedCatalogAreaCode = catalog.area_code
      this.selectedCatalogNumber = catalog.catalog_no
      this.isProductListModelActive = true
    },
    async onCreate(item) {
      try {
        const api = `${CATALOG_API}`

        this.sending = true
        await this.axios.post(api, item)

        this.isCreateModalActive = false

        notifyService.success(this.$t('attributes.add_succeed'))
        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    onDelete(item) {
      this.$buefy.dialog.confirm({
        message: this.$t('attributes.confirm_delete_with_name', {
          name: item.name
        }),
        onConfirm: () => {
          this.deleteItem(item)
        }
      })
    },
    async deleteItem(item) {
      try {
        const api = `${CATALOG_API}/${item.catalog_no}`

        this.sending = true
        await this.axios.delete(api)

        notifyService.success(this.$t('attributes.delete_succeed'))
        await this.loadItems()
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.sending = false
      }
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = url
    },
    rowClass(item) {
      return {
        'has-background-danger-light': !item.is_valid,
        'has-text-dark': !item.is_valid
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
